import { EGGS, ITEMS, WEAPONS } from "./content";
import type { BattleState } from "./types";

export const PROFILE_VERSION = 1;

export interface PlayerProfile {
  version: typeof PROFILE_VERSION;
  unlockedEggIds: string[];
  unlockedWeaponIds: string[];
  unlockedItemIds: string[];
  unlockedDangerLevel: 0 | 1 | 2 | 3 | 4 | 5;
  completedAchievementIds: string[];
  completedRunKeys: string[];
  victoriesByEgg: Record<string, number>;
  totalRuns: number;
  totalVictories: number;
  totalKills: number;
  totalMaterials: number;
  highestWave: number;
}

export interface UnlockReward {
  eggId?: string;
  weaponId?: string;
  itemId?: string;
}

export interface AchievementDefinition {
  id: string;
  title: string;
  titleZh: string;
  description: string;
  descriptionZh: string;
  reward?: UnlockReward;
  condition:
    | { type: "runs"; amount: number }
    | { type: "wave"; amount: number }
    | { type: "kills"; amount: number }
    | { type: "materials"; amount: number }
    | { type: "max-health"; amount: number }
    | { type: "dodge"; amount: number }
    | { type: "damage"; amount: number }
    | { type: "weapons"; amount: number }
    | { type: "victory"; eggId?: string; dangerLevel?: number };
}

export interface ProfileUpdate {
  profile: PlayerProfile;
  recorded: boolean;
  newAchievementIds: string[];
  rewards: UnlockReward[];
  unlockedDangerLevel: boolean;
}

const DEFAULT_EGGS = [
  "fresh-egg",
  "hard-boiled",
  "runner-egg",
  "scramble-egg",
  "sunny-egg",
];

const DEFAULT_WEAPONS = WEAPONS.slice(0, 12).map((entry) => entry.id);
const GATED_ITEM_IDS = new Set([
  "cast-iron-lid",
  "giant-carton",
  "titanium-shell",
  "jet-skates",
  "midnight-coffee",
  "dragon-sauce",
  "eternal-kettle",
  "sunrise-special",
  "black-hole-cup",
  "loaded-dice",
  "tablecloth-net",
  "slow-cooker",
  "miracle-butter",
  "perfect-breakfast",
]);
const DEFAULT_ITEMS = ITEMS.filter((entry) => !GATED_ITEM_IDS.has(entry.id)).map(
  (entry) => entry.id,
);

const CHALLENGE_ACHIEVEMENTS: readonly AchievementDefinition[] = [
  achievement("first-crack", "First Crack", "初次破壳", "Finish a run.", "完成一局游戏。", { type: "runs", amount: 1 }, { itemId: "cast-iron-lid" }),
  achievement("hold-five", "Breakfast Regular", "早餐常客", "Reach wave 5.", "到达第 5 波。", { type: "wave", amount: 5 }, { eggId: "poached-egg", weaponId: "coffee-sprayer" }),
  achievement("hold-ten", "Double Serving", "双份上桌", "Reach wave 10.", "到达第 10 波。", { type: "wave", amount: 10 }, { eggId: "double-yolk", weaponId: "soda-popper" }),
  achievement("collector", "Clean Plate", "光盘行动", "Collect 250 materials in one run.", "单局累计拾取 250 材料。", { type: "materials", amount: 250 }, { eggId: "magnet-egg", weaponId: "magnet-coil" }),
  achievement("fortress", "Uncrackable", "坚不可摧", "Reach 150 max shell.", "最大蛋壳达到 150。", { type: "max-health", amount: 150 }, { eggId: "giant-egg", weaponId: "salt-cannon" }),
  achievement("untouchable", "Untouchable", "毫发无损", "Reach 40% dodge.", "闪避达到 40%。", { type: "dodge", amount: 0.4 }, { eggId: "lucky-egg", weaponId: "rubber-spatula" }),
  achievement("overheat", "Over Easy", "火力全开", "Reach +80% damage.", "伤害加成达到 80%。", { type: "damage", amount: 1.8 }, { eggId: "spicy-egg", weaponId: "egg-timer" }),
  achievement("full-rack", "Royal Arsenal", "皇家军械库", "Equip six weapons.", "装备六把武器。", { type: "weapons", amount: 6 }, { eggId: "royal-egg", weaponId: "golden-ladle" }),
  achievement("thousand-served", "Crowd Control", "千份早餐", "Defeat 1,000 enemies across runs.", "累计击败 1000 个敌人。", { type: "kills", amount: 1_000 }, { itemId: "giant-carton" }),
] as const;

const VICTORY_REWARDS: Readonly<Record<string, UnlockReward>> = {
  "fresh-egg": { weaponId: "toaster-arc" },
  "hard-boiled": { itemId: "titanium-shell" },
  "runner-egg": { itemId: "jet-skates" },
  "scramble-egg": { itemId: "midnight-coffee" },
  "sunny-egg": { itemId: "dragon-sauce" },
  "poached-egg": { itemId: "eternal-kettle" },
  "double-yolk": { itemId: "sunrise-special" },
  "magnet-egg": { itemId: "black-hole-cup" },
  "giant-egg": { weaponId: "blender-blade" },
  "spicy-egg": { weaponId: "jam-jar" },
  "lucky-egg": { itemId: "loaded-dice" },
  "royal-egg": { weaponId: "breakfast-laser" },
};

const EGG_ZH_NAMES: Readonly<Record<string, string>> = {
  "fresh-egg": "新鲜蛋",
  "hard-boiled": "水煮硬蛋",
  "runner-egg": "飞毛腿蛋",
  "scramble-egg": "疾速炒蛋",
  "sunny-egg": "太阳蛋",
  "poached-egg": "水波蛋",
  "double-yolk": "双黄蛋",
  "magnet-egg": "磁力蛋",
  "giant-egg": "巨蛋",
  "spicy-egg": "辣味蛋",
  "lucky-egg": "幸运蛋",
  "royal-egg": "皇家蛋",
};

const DANGER_REWARDS: readonly UnlockReward[] = [
  { itemId: "tablecloth-net" },
  { itemId: "slow-cooker" },
  { weaponId: "milk-wand" },
  { itemId: "miracle-butter" },
  { itemId: "perfect-breakfast" },
];

export const ACHIEVEMENTS: readonly AchievementDefinition[] = [
  ...CHALLENGE_ACHIEVEMENTS,
  ...EGGS.map((egg) =>
    achievement(
      `victory:${egg.id}`,
      `${egg.name} Victory`,
      `${EGG_ZH_NAMES[egg.id] ?? egg.name}通关`,
      `Win a run with ${egg.name}.`,
      `使用${EGG_ZH_NAMES[egg.id] ?? egg.name}完成第 20 波。`,
      { type: "victory", eggId: egg.id },
      VICTORY_REWARDS[egg.id],
    ),
  ),
  ...DANGER_REWARDS.map((reward, index) =>
    achievement(
      `danger:${index + 1}`,
      `Danger ${index + 1}`,
      `危险 ${index + 1}`,
      `Win at Danger ${index + 1}.`,
      `在危险 ${index + 1} 中取得胜利。`,
      { type: "victory", dangerLevel: index + 1 },
      reward,
    ),
  ),
] as const;

export function createDefaultProfile(): PlayerProfile {
  return {
    version: PROFILE_VERSION,
    unlockedEggIds: [...DEFAULT_EGGS],
    unlockedWeaponIds: [...DEFAULT_WEAPONS],
    unlockedItemIds: [...DEFAULT_ITEMS],
    unlockedDangerLevel: 0,
    completedAchievementIds: [],
    completedRunKeys: [],
    victoriesByEgg: {},
    totalRuns: 0,
    totalVictories: 0,
    totalKills: 0,
    totalMaterials: 0,
    highestWave: 1,
  };
}

export function normalizeProfile(value: unknown): PlayerProfile {
  const fallback = createDefaultProfile();
  if (!isRecord(value) || value.version !== PROFILE_VERSION) return fallback;
  const unlockedEggIds = validIds(value.unlockedEggIds, EGGS.map((entry) => entry.id), DEFAULT_EGGS);
  const unlockedWeaponIds = validIds(value.unlockedWeaponIds, WEAPONS.map((entry) => entry.id), DEFAULT_WEAPONS);
  const unlockedItemIds = validIds(value.unlockedItemIds, ITEMS.map((entry) => entry.id), DEFAULT_ITEMS);
  const completedAchievementIds = validIds(
    value.completedAchievementIds,
    ACHIEVEMENTS.map((entry) => entry.id),
    [],
  );
  const completedRunKeys = Array.isArray(value.completedRunKeys)
    ? value.completedRunKeys
        .filter((key): key is string => typeof key === "string" && key.length <= 100)
        .slice(-50)
    : [];
  const victoriesByEgg: Record<string, number> = {};
  if (isRecord(value.victoriesByEgg)) {
    for (const egg of EGGS) {
      const count = value.victoriesByEgg[egg.id];
      if (safeCount(count)) victoriesByEgg[egg.id] = count;
    }
  }
  return {
    version: PROFILE_VERSION,
    unlockedEggIds,
    unlockedWeaponIds,
    unlockedItemIds,
    unlockedDangerLevel: boundedDanger(value.unlockedDangerLevel),
    completedAchievementIds,
    completedRunKeys,
    victoriesByEgg,
    totalRuns: safeCount(value.totalRuns) ? value.totalRuns : 0,
    totalVictories: safeCount(value.totalVictories) ? value.totalVictories : 0,
    totalKills: safeCount(value.totalKills) ? value.totalKills : 0,
    totalMaterials: safeCount(value.totalMaterials) ? value.totalMaterials : 0,
    highestWave: safeCount(value.highestWave) ? Math.max(1, value.highestWave) : 1,
  };
}

export function completeRun(
  currentProfile: PlayerProfile,
  state: BattleState,
  victory: boolean,
): ProfileUpdate {
  const profile = normalizeProfile(currentProfile);
  const runKey = `${state.seed}:${state.eggId}:${state.dangerLevel}`;
  if (profile.completedRunKeys.includes(runKey)) {
    return {
      profile,
      recorded: false,
      newAchievementIds: [],
      rewards: [],
      unlockedDangerLevel: false,
    };
  }
  profile.completedRunKeys.push(runKey);
  profile.completedRunKeys = profile.completedRunKeys.slice(-50);
  profile.totalRuns += 1;
  profile.totalKills += state.player.kills;
  profile.totalMaterials += state.player.materialsCollected;
  profile.highestWave = Math.max(profile.highestWave, state.wave);
  if (victory) {
    profile.totalVictories += 1;
    profile.victoriesByEgg[state.eggId] =
      (profile.victoriesByEgg[state.eggId] ?? 0) + 1;
  }

  const previousDanger = profile.unlockedDangerLevel;
  if (victory && state.dangerLevel >= profile.unlockedDangerLevel) {
    profile.unlockedDangerLevel = Math.min(5, state.dangerLevel + 1) as PlayerProfile["unlockedDangerLevel"];
  }

  const newAchievementIds: string[] = [];
  const rewards: UnlockReward[] = [];
  for (const achievementDefinition of ACHIEVEMENTS) {
    if (profile.completedAchievementIds.includes(achievementDefinition.id)) continue;
    if (!achievementMet(achievementDefinition, profile, state, victory)) continue;
    profile.completedAchievementIds.push(achievementDefinition.id);
    newAchievementIds.push(achievementDefinition.id);
    if (achievementDefinition.reward) {
      rewards.push(achievementDefinition.reward);
      applyReward(profile, achievementDefinition.reward);
    }
  }

  return {
    profile,
    recorded: true,
    newAchievementIds,
    rewards,
    unlockedDangerLevel: profile.unlockedDangerLevel > previousDanger,
  };
}

function achievementMet(
  definition: AchievementDefinition,
  profile: PlayerProfile,
  state: BattleState,
  victory: boolean,
): boolean {
  const condition = definition.condition;
  if (condition.type === "runs") return profile.totalRuns >= condition.amount;
  if (condition.type === "wave") return state.wave >= condition.amount;
  if (condition.type === "kills") return profile.totalKills >= condition.amount;
  if (condition.type === "materials") return state.player.materialsCollected >= condition.amount;
  if (condition.type === "max-health") return state.player.maxHealth >= condition.amount;
  if (condition.type === "dodge") return state.player.dodgeChance >= condition.amount;
  if (condition.type === "damage") return state.player.damageMultiplier >= condition.amount;
  if (condition.type === "weapons") return state.weapons.length >= condition.amount;
  return (
    victory &&
    (condition.eggId === undefined || condition.eggId === state.eggId) &&
    (condition.dangerLevel === undefined || state.dangerLevel >= condition.dangerLevel)
  );
}

function applyReward(profile: PlayerProfile, reward: UnlockReward): void {
  if (reward.eggId) addUnique(profile.unlockedEggIds, reward.eggId);
  if (reward.weaponId) addUnique(profile.unlockedWeaponIds, reward.weaponId);
  if (reward.itemId) addUnique(profile.unlockedItemIds, reward.itemId);
}

function achievement(
  id: string,
  title: string,
  titleZh: string,
  description: string,
  descriptionZh: string,
  condition: AchievementDefinition["condition"],
  reward?: UnlockReward,
): AchievementDefinition {
  return { id, title, titleZh, description, descriptionZh, condition, ...(reward ? { reward } : {}) };
}

function validIds(value: unknown, known: readonly string[], fallback: readonly string[]): string[] {
  if (!Array.isArray(value)) return [...fallback];
  return [...new Set([...fallback, ...value.filter((id): id is string => typeof id === "string" && known.includes(id))])];
}

function addUnique(values: string[], value: string): void {
  if (!values.includes(value)) values.push(value);
}

function boundedDanger(value: unknown): PlayerProfile["unlockedDangerLevel"] {
  return typeof value === "number" && Number.isInteger(value)
    ? Math.max(0, Math.min(5, value)) as PlayerProfile["unlockedDangerLevel"]
    : 0;
}

function safeCount(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 && value <= 1_000_000_000;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
